<script context="module">
  export function asDatestamp(date) {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return [year, month, day]
      .map((val) => String(val).padStart(2, '0'))
      .join('-')
  }

  /** @type {function(string): Date} */
  export function parseDate(value) {
    const [year, month, day] = String(value).split('-')
    return new Date(+year, +month - 1, +day)
  }

  export function getDefaultDate() {
    const date = addDays(new Date(), 40)
    const day = date.getDay()
    return asDatestamp(
      day !== 3 ? addDays(date, day > 3 ? 7 - day + 3 : 3 - day) : date
    )
  }
</script>

<script>
  import { createEventDispatcher } from 'svelte'
  import {
    differenceInCalendarDays,
    getDaysInMonth,
    startOfMonth,
    startOfWeek,
    isSameMonth,
    endOfMonth,
    addMonths,
    addDays
  } from 'date-fns'

  import { getLanguage } from '$lib/context.js'
  import Symbol from '$lib/Symbol.svelte'
  import { text } from '$lib/text.js'

  /** @type {import('svelte').EventDispatcher<{ change: string, view: string }>} */
  const dispatch = createEventDispatcher()
  const language = getLanguage()
  const today = new Date()
  const tomorrow = addDays(today, 1)
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 })
  const weekdays = Array(7)
    .fill(null)
    .map((_, i) => new Date(new Date(monday).setDate(monday.getDate() + i)))

  /** @type {function(Date): (null|Date)[][]} */
  const getWeeks = (month) => {
    /** @type {(null|Date)[][]} */
    const weeks = [[]]
    const last = endOfMonth(month)
    const first = startOfMonth(month)
    const daysInMonth = getDaysInMonth(month)
    const max = daysInMonth + 7 - (last.getDay() || 7)

    for (let i = ((first.getDay() || 7) - 1) * -1; i < max; i++) {
      let week = weeks.at(-1)
      if (i < 0 || i >= daysInMonth) {
        week?.push(null)
      } else {
        const day = addDays(first, i)
        if (day.getDay() === 1 && week?.length) weeks.push([day])
        else week?.push(day)
      }
    }

    return weeks
  }

  export let name

  /** @type {string?} */
  export let max = null

  /** @type {string?} */
  export let min = asDatestamp(tomorrow)

  /** @type {string} */
  export let value = min || getDefaultDate()

  let offset = 0

  $: minDate = min ? parseDate(min) : null
  $: maxDate = max ? parseDate(max) : null
  $: selected = addMonths(parseDate(value), offset)
  $: prev = endOfMonth(addMonths(selected, -1))
  $: next = startOfMonth(addMonths(selected, 1))
  $: diffStart = differenceInCalendarDays(startOfMonth(selected), today)
  $: diffEnd = differenceInCalendarDays(endOfMonth(selected), today)
  $: maybe = diffEnd >= 90 && diffStart < 180
  $: unlikely = diffEnd >= 180
  $: weeks = getWeeks(selected)
</script>

<div class="datepicker">
  <div class="header">
    <div class="left">
      {#if !minDate || !isSameMonth(selected, minDate)}
        <button
          {name}
          class="month"
          value={asDatestamp(prev)}
          on:click|preventDefault={() => {
            offset--
            dispatch('view', asDatestamp(prev))
          }}>
          <Symbol name="chevron" rotate="left" />
          {prev.toLocaleString($language, { month: 'short' })}
        </button>
      {/if}
    </div>
    <div class="center">
      <strong>
        {selected.toLocaleString($language, { month: 'long' })}
      </strong>
      {selected.getFullYear()}
    </div>
    <div class="right">
      {#if !maxDate || !isSameMonth(selected, maxDate)}
        <button
          {name}
          class="month"
          value={asDatestamp(next)}
          on:click|preventDefault={() => {
            offset++
            dispatch('view', asDatestamp(next))
          }}>
          {next.toLocaleString($language, { month: 'short' })}
          <Symbol name="chevron" rotate="right" />
        </button>
      {/if}
    </div>
  </div>
  <table cellpadding="0" cellspacing="0">
    <thead>
      <tr>
        {#each weekdays as day}
          <th>{day.toLocaleString($language, { weekday: 'short' })}</th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each weeks as week}
        <tr>
          {#each week as day}
            {@const diff = day && differenceInCalendarDays(day, today)}
            {@const maybe = diff && diff > 90 && diff <= 180}
            {@const unlikely = diff && diff > 180}
            <td class:maybe class:unlikely>
              {#if day}
                {@const _value = asDatestamp(day)}
                {@const selected = _value === value}
                {@const disabled = Boolean(
                  (min && _value < min) || (max && _value > max)
                )}
                <button
                  {name}
                  {disabled}
                  class="day"
                  value={_value}
                  on:click|preventDefault={() => {
                    dispatch('change', _value)
                    value = _value
                    offset = 0
                  }}>
                  <slot value={_value} {selected} {disabled}>
                    <span class="value" class:selected>
                      {day.getDate()}
                      <span class="u-hidden">
                        {#if maybe}*{/if}{#if unlikely}**{/if}
                      </span>
                    </span>
                  </slot>
                </button>
              {/if}
            </td>
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
  <div class="footer" class:maybe class:unlikely>
    {#if maybe}
      <p class="footnote maybe">
        <span class="u-hidden">*</span>
        {$text`Tickets may not be available yet`}
      </p>
    {/if}
    {#if unlikely}
      <p class="footnote unlikely">
        <span class="u-hidden">**</span>
        {$text`Too far in the future`}
      </p>
    {/if}
  </div>
</div>

<style>
  .datepicker {
    display: grid;
    gap: 1.25rem;
    container-type: inline-size;
  }

  .header {
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    height: 2rem;
    align-items: center;
  }

  .center {
    height: 100%;
    font-weight: 600;
    font-size: 1.25rem;
    text-align: center;
  }

  .right,
  .left {
    display: flex;
    height: 100%;
    margin-top: -0.3rem;
  }

  .right:active,
  .left:active {
    opacity: 0.7;
  }

  .right {
    justify-content: end;
    text-align: right;
  }

  .month {
    display: inline-flex;
    align-items: center;
  }

  table {
    width: 100%;
    text-align: center;
  }

  thead {
    font-size: 0.75rem;
    color: rgb(var(--rgb-muted));
    text-transform: uppercase;
  }

  th {
    padding: 0.5em;
    border-bottom: 1px solid rgba(var(--rgb-muted), 0.2);
    font-weight: 600;
    letter-spacing: 0.03rem;
  }

  @container (width > 30rem) {
    th {
      font-size: 0.8rem;
    }
  }

  td {
    position: relative;
    padding-top: 1%;
  }

  tbody tr:first-child td {
    border-top: 0.4rem solid transparent;
  }

  td.maybe::before,
  td.unlikely::before {
    content: '';
    position: absolute;
    inset: 0.3rem 0 0;
  }

  td.maybe:first-child::before,
  td:not(.maybe) + td.maybe::before,
  td.unlikely:first-child::before,
  td:not(.unlikely) + td.unlikely::before {
    border-top-left-radius: var(--border-radius-sm);
    border-bottom-left-radius: var(--border-radius-sm);
  }

  td.maybe:last-child::before,
  td.maybe:not(:has(~ td.maybe))::before,
  td.unlikely:last-child::before,
  td.unlikely:not(:has(~ td.unlikely))::before {
    border-top-right-radius: var(--border-radius-sm);
    border-bottom-right-radius: var(--border-radius-sm);
  }

  td.maybe::before {
    background-color: rgba(255, 204, 51, 0.15);
  }

  td.unlikely::before {
    background-color: rgba(252, 45, 84, 0.1);
  }

  .day {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
  }

  .day::before {
    content: '';
    position: absolute;
    inset: 0;
  }

  .day:disabled {
    cursor: default;
  }

  .value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    line-height: 1;
    border-radius: var(--border-radius-full);
    transition: var(--background-transition);
    transform-origin: center;
  }

  .day:disabled .value {
    color: rgba(var(--rgb-muted), 0.4);
  }

  .day:hover:not(:disabled) .value:not(.selected) {
    background-color: rgba(var(--rgb-muted), 0.2);
  }

  .day:not(:disabled):active .value {
    color: rgba(0, 0, 0, 0.6);
    transform: scale(0.93);
    transition:
      scale 50ms var(--ease-out),
      color 50ms var(--ease-out);
  }

  .selected {
    font-weight: bold;
    color: rgb(var(--rgb-white));
    background-color: rgb(var(--rgb-secondary, var(--rgb-blue)));
  }

  td.maybe:hover .day:not(:disabled) .value {
    background-color: rgb(255, 204, 51);
  }

  td.maybe .day:not(:disabled) .value.selected {
    color: rgb(var(--rgb-text));
    background-color: rgba(255, 235, 177);
  }

  td.unlikely:hover .day:not(:disabled) .value {
    background-color: rgb(246, 145, 165);
  }

  td.unlikely .day:not(:disabled) .value.selected {
    color: rgb(var(--rgb-text));
    background-color: rgba(249, 209, 217);
  }

  .footer:not(.maybe):not(.unlikely) {
    display: none;
  }

  .footnote {
    display: flex;
    gap: 0.4rem;
    align-items: center;
  }

  .footnote::before {
    content: '';
    width: 0.8rem;
    height: 0.8rem;
    border-radius: var(--border-radius-full);
  }

  .footnote.maybe::before {
    background-color: rgb(255, 204, 51);
  }

  .footnote.unlikely::before {
    background-color: rgb(255, 35, 70, 59%);
  }
</style>
